import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GlobalSearch from "./GlobalSearch";
import NotificationBell from "./NotificationBell";

/**
 * THE ONE APPLICATION HEADER (Part 1 §2).
 *
 * Mounted once by `AdminLayout` and never re-mounted, so it persists across every navigation — the
 * search box keeps its text and the unread count keeps its cache while the reader moves between
 * pages.
 *
 * ── ONE HEADER, NOT A DESKTOP ONE AND A MOBILE ONE (§2) ─────────────────────
 *
 *   Desktop   VVR │ menu │ Global Search ………… │ Notifications │ User
 *   Mobile    menu │ VVR │ Search │ Notifications │ User
 *
 * Those are the SAME ELEMENTS in the same order with different widths — not two implementations.
 * There is no `isMobile` branch anywhere in this file and no second component: the menu button is
 * `d-lg-none` because the sidebar is persistent above 992px and a drawer below it (`index.css`), the
 * brand's wordmark hides below 576px, and the search box shrinks by CSS. Every child — search, bell,
 * user menu — is rendered exactly once and behaves identically at every width, so no business logic
 * is duplicated between the two shapes and neither can drift.
 *
 * ── WHAT WAS ALREADY HERE IS UNCHANGED ──────────────────────────────────────
 * The menu toggle keeps its accessible name and its `aria-expanded`; the user dropdown keeps its
 * name, email and the same `logout()` → `/login` sign-out. §2 asks for those to be preserved, and
 * they are — what is added around them is the brand, the search control and the notification entry
 * point.
 */
export default function Header({ onToggleSidebar, sidebarOpen = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="admin-header">
      {/* Icon-only, and it is the ONLY way to reach navigation on a narrow viewport — so its name
          matters more here than anywhere else. `title` alone is an unreliable accessible name; some
          screen readers ignore it on an element with no text, which would leave the menu announced
          as just "button". `aria-expanded` reports whether the sidebar is currently open. */}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary d-lg-none order-1"
        onClick={onToggleSidebar}
        title="Menu"
        aria-label="Toggle navigation menu"
        aria-expanded={sidebarOpen ? "true" : "false"}
      >
        <i className="bi bi-list" aria-hidden="true" />
      </button>

      {/* THE BRAND IS A LINK TO THE DASHBOARD, which is what a masthead is expected to be. `order-2`
          puts it after the menu button on mobile and, on desktop where the button is absent, first —
          giving §2's two orders from one DOM order. The wordmark hides on the narrowest viewports so
          the search box keeps a usable width; the icon always remains, so the brand never vanishes. */}
      <Link to="/" className="header-brand order-2" aria-label="VVR ERP — Dashboard">
        <i className="bi bi-graph-up-arrow" aria-hidden="true" />
        <span className="header-brand-name d-none d-sm-inline">VVR</span>
      </Link>

      <div className="header-search order-3">
        <GlobalSearch />
      </div>

      <div className="order-4 d-flex align-items-center gap-2">
        <NotificationBell />

        <div className="dropdown">
          <button className="btn btn-sm btn-light dropdown-toggle" data-bs-toggle="dropdown">
            <i className="bi bi-person-circle me-1" />
            <span className="d-none d-md-inline">{user?.fullName || "Account"}</span>
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            <li className="dropdown-header">
              <div className="fw-semibold">{user?.fullName}</div>
              <div className="small text-muted">{user?.email}</div>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button className="dropdown-item" onClick={signOut}>
                <i className="bi bi-box-arrow-right me-2" />
                Sign out
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}
